import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { once } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { MetricValueType, TimeUnit } from 'webapp/enums/enums';

import didUpdate from 'webapp/modifiers/did-update';
import FormSelectComponent from 'webapp/components/form/select';
import type { OptionRaw } from 'webapp/components/form/select';

import DateRange from 'webapp/models-custom/date-range';

export interface MetricTypePropertiesType {
	metricType: MetricValueType | string;
	aggregationFunction: string | null;
	granulationOrigin: TimeUnit | null;
}

interface Args {
	model: MetricTypePropertiesType;
	submitInProgress: boolean;
	onChange: (metricTypeProperties: MetricTypePropertiesType) => void;
	hidePeriodDataType?: boolean;
	hideAllValuesDataType?: boolean;
	theme?: string;
}

// to show in dropdown
const AggregationFunction = {
	LATEST: 'LATEST',
	SUM: 'SUM',
};

const DataType = {
	ALL: 'all',
	PERIOD: 'period',
};

export default class MetricEditMetricTypePropertiesComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;

	@tracked aggregationFunction = AggregationFunction.SUM;
	@tracked dataType = DataType.ALL;
	@tracked granulationOrigin: TimeUnit | null = null;

	granulationOriginOptions: OptionRaw<TimeUnit>[] = [];
	metricType = '';

	get theme() {
		return this.args.theme || undefined;
	}

	get aggregationFunctionOptions(): LabelValuePairString[] {
		const options: LabelValuePairString[] = [];

		if (!this.args.hideAllValuesDataType) {
			// because latest only allows all values
			options.push({ value: AggregationFunction.LATEST, label: 'Latest' });
		}

		options.push(
			{ value: AggregationFunction.SUM, label: 'SUM' },
			{ value: 'MIN', label: 'MIN' },
			{ value: 'MAX', label: 'MAX' },
			{ value: 'AVG', label: 'AVG' },
			{ value: 'COUNT', label: 'Count' },
		);

		return options;
	}

	get dataTypeOptions(): LabelValuePairString[] {
		const options: LabelValuePairString[] = [];

		if (!this.args.hideAllValuesDataType) {
			options.push({
				value: DataType.ALL,
				label: 'All values',
			});
		}

		if (
			!this.args.hidePeriodDataType &&
			this.aggregationFunction !== AggregationFunction.LATEST
		) {
			options.push({
				value: DataType.PERIOD,
				label: 'Period values',
			});
		}

		return options;
	}

	get showGranulationOrigin(): boolean {
		return this.dataType === DataType.PERIOD;
	}

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		if (this.args.hideAllValuesDataType) {
			this.dataType = DataType.PERIOD;
		}

		this.granulationOriginOptions = DateRange.baseGranulations
			.filter((item) => item.id !== TimeUnit.TOTAL)
			.map((granulation) => {
				return { value: granulation.id, label: granulation.title };
			});

		this.updateLocalValues();
	}

	triggerOnChange(): void {
		this.args.onChange({
			metricType: this.metricType,
			granulationOrigin: this.granulationOrigin,
			aggregationFunction: this.aggregationFunction,
		});
	}

	updateMetricType(): void {
		if (this.aggregationFunction === AggregationFunction.LATEST) {
			this.metricType = MetricValueType.CURRENT;
		} else {
			if (this.dataType === DataType.ALL) {
				this.metricType = MetricValueType.EVENT;
			} else {
				this.metricType = MetricValueType.GENERAL;
			}
		}

		once(this, this.triggerOnChange);
	}

	@action
	updateLocalValues(): void {
		this.granulationOrigin = this.args.model.granulationOrigin;
		this.metricType = this.args.model.metricType;

		if (this.metricType === MetricValueType.CURRENT) {
			this.aggregationFunction = AggregationFunction.LATEST;
		} else {
			this.aggregationFunction =
				this.args.model.aggregationFunction || AggregationFunction.SUM;
		}

		this.dataType = this.granulationOrigin ? DataType.PERIOD : DataType.ALL;
	}

	@action
	changeDataType(value: string): void {
		this.dataType = value;

		if (this.dataType === DataType.ALL) {
			this.changeGranulationOrigin(null);
		} else {
			if (!this.granulationOrigin) {
				this.changeGranulationOrigin(TimeUnit.DAY);
			}
		}

		this.updateMetricType();
	}

	@action
	changeAggregationFunction(value: string): void {
		this.aggregationFunction = value;

		if (AggregationFunction.LATEST === value) {
			this.changeDataType(DataType.ALL);
		} else {
			this.updateMetricType();
		}
	}

	@action
	changeGranulationOrigin(value: TimeUnit | null): void {
		this.granulationOrigin = value;

		once(this, this.triggerOnChange);
	}
	<template>
		<div
			class="parts-flex parts-flex--{{if this.showGranulationOrigin '3' '2'}}-items"
			{{didUpdate this.updateLocalValues @model}}
		>
			<div class="part">
				<FormSelectComponent
					name="agg_fn"
					@selected={{this.aggregationFunction}}
					@options={{this.aggregationFunctionOptions}}
					@onChange={{this.changeAggregationFunction}}
					@isNumeric={{false}}
					@isDisabled={{@submitInProgress}}
					@placeholder="Aggregation function"
					@isRequired={{true}}
					@theme={{this.theme}}
				/>
			</div>
			<div class="seperator seperator--first">of</div>
			<div class="part">
				<FormSelectComponent
					name="data_type"
					@selected={{this.dataType}}
					@options={{this.dataTypeOptions}}
					@onChange={{this.changeDataType}}
					@isNumeric={{false}}
					@isDisabled={{@submitInProgress}}
					@placeholder="Data type"
					@isRequired={{true}}
					@theme={{this.theme}}
				/>
			</div>
			{{#if this.showGranulationOrigin}}
				<div class="seperator seperator--second">for the</div>
				<div class="part">
					<FormSelectComponent
						name="granulation_origin"
						@selected={{this.granulationOrigin}}
						@options={{this.granulationOriginOptions}}
						@onChange={{this.changeGranulationOrigin}}
						@isDisabled={{@submitInProgress}}
						@placeholder="Period"
						@isRequired={{true}}
						@theme={{this.theme}}
					/>
				</div>
			{{/if}}
		</div>
	</template>
}
